import { Checkbox, Typography } from '@sonnen/web-ui';
import Markdown from 'react-markdown';
import { useAppContext } from '../../../AppContext/AppContext';
import { CurrentUserSiteOptionsCreateContractFlow } from '../../../graphql/generated';
import { TValidationResult } from '../../../hooks/useValidation';
import { useTranslation } from '../../../i18n/i18n';
import { OrderFlow, TFormValues } from '../../../types';
import { ExternalLinkComponent } from '../../../utils/utils';
import commonStyles from '../../Common.module.css';

const I18N_SCOPE = 'data.paymentDetails';

type PickedValues = Pick<
  TFormValues,
  'paymentAuthorization' | 'termsAndConditions' | 'marketing' | 'bindingContract'
>;

type CheckboxesProps = {
  values: PickedValues;
  onChange: (field: string, value: boolean) => void;
  errors: TValidationResult<PickedValues>;
};

export const Checkboxes = ({ values, onChange, errors }: CheckboxesProps) => {
  const { t } = useTranslation();
  const { orderFlow, user, selectedSite } = useAppContext();
  const { paymentAuthorization, marketing, termsAndConditions, bindingContract } = values;

  return (
    <div data-testid="checkboxes">
      {orderFlow !== OrderFlow.selfConsumptionPlus && (
        <Checkbox
          name="paymentAuthorization"
          onChange={() => onChange('paymentAuthorization', !paymentAuthorization)}
          checked={paymentAuthorization}
          error={Boolean(errors.paymentAuthorization)}
          errorLabel={errors.paymentAuthorization}
          dataTestId="payment-authorization"
          clickableLabel
          className={commonStyles.checkBox}
        >
          <Typography.Body2>{t(`${I18N_SCOPE}.paymentAuthorization`)}</Typography.Body2>
        </Checkbox>
      )}

      {selectedSite?.options.createContractFlow ===
        CurrentUserSiteOptionsCreateContractFlow.SwitchFromFlatBindingContract && (
        <Checkbox
          name="bindingContract"
          onChange={() => onChange('bindingContract', !bindingContract)}
          checked={Boolean(bindingContract)}
          error={Boolean(errors.bindingContract)}
          errorLabel={errors.bindingContract}
          dataTestId="binding-contract"
          className={commonStyles.checkBox}
          clickableLabel
        >
          <Typography.Body2>{t(`${I18N_SCOPE}.bindingContract`)}</Typography.Body2>
        </Checkbox>
      )}

      <Checkbox
        name="termsAndConditions"
        onChange={() => onChange('termsAndConditions', !termsAndConditions)}
        checked={termsAndConditions}
        error={Boolean(errors.termsAndConditions)}
        errorLabel={errors.termsAndConditions}
        dataTestId="terms-and-conditions"
        clickableLabel
        className={commonStyles.checkBox}
      >
        <Typography.Body2>
          <Markdown components={{ a: ExternalLinkComponent }}>
            {t(`${I18N_SCOPE}.termsAndConditions`)}
          </Markdown>
        </Typography.Body2>
      </Checkbox>

      {(!user || orderFlow === OrderFlow.selfConsumptionPlus) && (
        <Checkbox
          name="marketing"
          onChange={() => onChange('marketing', !marketing)}
          checked={Boolean(marketing)}
          dataTestId="marketing"
          clickableLabel
          className={commonStyles.checkBox}
        >
          <Typography.Body2>
            <Markdown components={{ a: ExternalLinkComponent }}>
              {t(`${I18N_SCOPE}.marketing`)}
            </Markdown>
          </Typography.Body2>
        </Checkbox>
      )}
    </div>
  );
};
